import {
  parseAdminOccurrenceDetail,
  parseAdminLessonDetail,
  parseAdminLessonsResponse,
  parseAdminRecurringSlot,
  parseAdminRecurringSlotsResponse,
  parseCreateAdminLessonRequest,
  parseCreateManualOccurrenceRequest,
  parseCreateManualOccurrencesResponse,
  parseCreateRecurringSlotRequest,
  parseTransitionLessonStatusRequest,
  parseTransitionRecurringSlotStatusRequest,
  parseUpdateAdminLessonRequest,
  parseUpdateRecurringSlotRequest
} from "@madamuh/domain";
import type {
  AdminOccurrenceDetailDto,
  AdminLessonDetailDto,
  AdminLessonsResponseDto,
  AdminRecurringSlotDto,
  AdminRecurringSlotsResponseDto,
  CreateAdminLessonRequestDto,
  CreateManualOccurrenceRequestDto,
  CreateManualOccurrencesResponseDto,
  CreateRecurringSlotRequestDto,
  TransitionLessonStatusRequestDto,
  TransitionRecurringSlotStatusRequestDto,
  UpdateAdminLessonRequestDto,
  UpdateRecurringSlotRequestDto
} from "@madamuh/domain";
import { apiGet, apiPatch, apiPost } from "./client";

export async function fetchAdminLessons(tenantSlug: string): Promise<AdminLessonsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/lessons`);
  return parseAdminLessonsResponse(response);
}

export async function fetchAdminLesson(tenantSlug: string, lessonId: string): Promise<AdminLessonDetailDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/lessons/${lessonId}`);
  return parseAdminLessonDetail(response);
}

export async function createAdminLesson(
  tenantSlug: string,
  request: CreateAdminLessonRequestDto
): Promise<AdminLessonDetailDto> {
  const parsedRequest = parseCreateAdminLessonRequest(request);
  const response = await apiPost<CreateAdminLessonRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/lessons`,
    parsedRequest
  );

  return parseAdminLessonDetail(response);
}

export async function patchAdminLesson(
  tenantSlug: string,
  lessonId: string,
  request: UpdateAdminLessonRequestDto
): Promise<AdminLessonDetailDto> {
  const parsedRequest = parseUpdateAdminLessonRequest(request);
  const response = await apiPatch<UpdateAdminLessonRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/lessons/${lessonId}`,
    parsedRequest
  );

  return parseAdminLessonDetail(response);
}

export async function transitionAdminLesson(
  tenantSlug: string,
  lessonId: string,
  request: TransitionLessonStatusRequestDto
): Promise<AdminLessonDetailDto> {
  const parsedRequest = parseTransitionLessonStatusRequest(request);
  const response = await apiPost<TransitionLessonStatusRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/lessons/${lessonId}/transition`,
    parsedRequest
  );

  return parseAdminLessonDetail(response);
}

export async function fetchAdminRecurringSlots(tenantSlug: string): Promise<AdminRecurringSlotsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/recurring-slots`);
  return parseAdminRecurringSlotsResponse(response);
}

export async function fetchAdminRecurringSlotsForLesson(
  tenantSlug: string,
  lessonId: string
): Promise<AdminRecurringSlotsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/lessons/${lessonId}/recurring-slots`);
  return parseAdminRecurringSlotsResponse(response);
}

export async function fetchAdminRecurringSlot(
  tenantSlug: string,
  slotId: string
): Promise<AdminRecurringSlotDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/recurring-slots/${slotId}`);
  return parseAdminRecurringSlot(response);
}

export async function createAdminRecurringSlot(
  tenantSlug: string,
  request: CreateRecurringSlotRequestDto
): Promise<AdminRecurringSlotDto> {
  const parsedRequest = parseCreateRecurringSlotRequest(request);
  const response = await apiPost<CreateRecurringSlotRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/recurring-slots`,
    parsedRequest
  );

  return parseAdminRecurringSlot(response);
}

export async function createAdminRecurringSlotForLesson(
  tenantSlug: string,
  lessonId: string,
  request: Omit<CreateRecurringSlotRequestDto, "lessonId"> & { lessonId?: string }
): Promise<AdminRecurringSlotDto> {
  const parsedRequest = parseCreateRecurringSlotRequest({
    ...request,
    lessonId
  });
  const response = await apiPost<CreateRecurringSlotRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/lessons/${lessonId}/recurring-slots`,
    parsedRequest
  );

  return parseAdminRecurringSlot(response);
}

export async function patchAdminRecurringSlot(
  tenantSlug: string,
  slotId: string,
  request: UpdateRecurringSlotRequestDto
): Promise<AdminRecurringSlotDto> {
  const parsedRequest = parseUpdateRecurringSlotRequest(request);
  const response = await apiPatch<UpdateRecurringSlotRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/recurring-slots/${slotId}`,
    parsedRequest
  );

  return parseAdminRecurringSlot(response);
}

export async function transitionAdminRecurringSlot(
  tenantSlug: string,
  slotId: string,
  request: TransitionRecurringSlotStatusRequestDto
): Promise<AdminRecurringSlotDto> {
  const parsedRequest = parseTransitionRecurringSlotStatusRequest(request);
  const response = await apiPost<TransitionRecurringSlotStatusRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/recurring-slots/${slotId}/transition`,
    parsedRequest
  );

  return parseAdminRecurringSlot(response);
}

export async function createAdminManualOccurrence(
  tenantSlug: string,
  lessonId: string,
  request: CreateManualOccurrenceRequestDto
): Promise<CreateManualOccurrencesResponseDto> {
  const parsedRequest = parseCreateManualOccurrenceRequest(request);
  const response = await apiPost<CreateManualOccurrenceRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/lessons/${lessonId}/manual-occurrences`,
    parsedRequest
  );

  return parseCreateManualOccurrencesResponse(response);
}

export async function cancelAdminManualOccurrence(
  tenantSlug: string,
  occurrenceId: string
): Promise<AdminOccurrenceDetailDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/occurrences/${occurrenceId}/cancel-manual`,
    {}
  );

  return parseAdminOccurrenceDetail(response);
}
